import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { performanceAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Loader2, Target, Edit } from 'lucide-react';

const MyGoals = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    key_results: '',
    weightage: 0,
    cycle_id: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [goalsRes, reviewsRes, cyclesRes] = await Promise.all([
        performanceAPI.getGoals({ employee_id: user.employee_id }),
        performanceAPI.getReviews({ employee_id: user.employee_id }),
        performanceAPI.getCycles({ status_filter: 'ACTIVE' }),
      ]);
      setGoals(goalsRes.data);
      setReviews(reviewsRes.data);
      setCycles(cyclesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await performanceAPI.createGoal({
        ...formData,
        employee_id: user.employee_id,
        company_id: user.company_id,
        key_results: formData.key_results.split('\n').filter(kr => kr.trim()),
      });
      toast.success('Goal created');
      setIsCreateOpen(false);
      setFormData({
        title: '',
        description: '',
        key_results: '',
        weightage: 0,
        cycle_id: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create goal');
    } finally {
      setCreateLoading(false);
    }
  };

  const statusColors = {
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-green-100 text-green-800',
    ON_HOLD: 'bg-yellow-100 text-yellow-800',
    PENDING: 'bg-gray-100 text-gray-800',
    SELF_REVIEW_DONE: 'bg-purple-100 text-purple-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Goals & Reviews</h1>
          <p className="text-muted-foreground">Track your goals and performance reviews</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-goal-btn" disabled={cycles.length === 0}>
              <Plus className="mr-2 h-4 w-4" />
              Add Goal
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Goal</DialogTitle>
              <DialogDescription>
                Add a new goal for the current performance cycle
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateGoal}>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label>Performance Cycle *</Label>
                  <Select
                    value={formData.cycle_id}
                    onValueChange={(v) => setFormData({ ...formData, cycle_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      {cycles.map((cycle) => (
                        <SelectItem key={cycle.id} value={String(cycle.id)}>
                          {cycle.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Goal Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Improve customer satisfaction"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your goal"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Key Results (one per line)</Label>
                  <Textarea
                    value={formData.key_results}
                    onChange={(e) => setFormData({ ...formData, key_results: e.target.value })}
                    placeholder="e.g., Achieve NPS score of 80+"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weightage (%)</Label>
                  <Input
                    type="number"
                    value={formData.weightage}
                    onChange={(e) => setFormData({ ...formData, weightage: Number(e.target.value) })}
                    min={0}
                    max={100}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createLoading || !formData.cycle_id}>
                  {createLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="goals" className="space-y-6">
        <TabsList>
          <TabsTrigger value="goals">My Goals</TabsTrigger>
          <TabsTrigger value="reviews">My Reviews</TabsTrigger>
        </TabsList>

        <TabsContent value="goals">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {goals.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="text-center py-10 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No goals found. Create your first goal!</p>
                </CardContent>
              </Card>
            ) : (
              goals.map((goal) => (
                <Card key={goal.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{goal.title}</CardTitle>
                      <Badge className={statusColors[goal.status] || ''}>
                        {goal.status?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {goal.description && (
                      <p className="text-sm text-muted-foreground mb-3">{goal.description}</p>
                    )}
                    {goal.key_results?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Key Results:</p>
                        <ul className="text-sm space-y-1">
                          {goal.key_results.map((kr, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                              {kr}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {goal.weightage > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Weightage: {goal.weightage}%
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          <Card>
            <CardHeader>
              <CardTitle>Performance Reviews</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Self Rating</TableHead>
                    <TableHead>Manager Rating</TableHead>
                    <TableHead>Final Rating</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                        No reviews found
                      </TableCell>
                    </TableRow>
                  ) : (
                    reviews.map((review) => (
                      <TableRow key={review.id}>
                        <TableCell className="font-medium">{review.cycle_name}</TableCell>
                        <TableCell>{review.self_rating?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell>{review.manager_rating?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell>{review.final_rating?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[review.status] || ''}>
                            {review.status?.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyGoals;
